import React, { useContext, useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { BaseURL } from "../../../../Files/BaseURL";
import AlertContext from "../../../../Context/Alert/AlertContext";
import ThemeContext from "../../../../Context/theme/ThemeContext";

const MarketPlaceCreation = () => {
  const { theme } = useContext(ThemeContext);
  const navigate = useNavigate();
  const location = useLocation();
  const [IndustryList, setIndustryList] = useState([]);
  const [ServiceSelect, setServiceSelect] = useState(null);
  const [ServiceSkill, setServiceSkill] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isInitializing, setIsInitializing] = useState(true);

  const [formData, setFormData] = useState({
    title: "",
    category: "Category",
    subcategory: "SubCategory",
    keywords: [],
    keyword: "",
  });

  const alertContext = useContext(AlertContext);
  const { showAlert } = alertContext;

  const fetchIndustry = async () => {
    try {
      const response = await axios.get(
        `${BaseURL}/api/auth/servicecategoryname`
      );
      setIndustryList(response.data);
    } catch (error) {
      showAlert(
        error.response?.data?.message || "Error fetching categories",
        "danger"
      );
    } finally {
      setIsInitializing(false);
    }
  };

  const fetchServiceSkills = async () => {
    try {
      setIsLoading(true);
      const response = await axios.get(
        `${BaseURL}/api/auth/servicecategoryDetail/${ServiceSelect}`
      );
      const data = response.data;
      const subSkills = [];
      data.Skills.forEach((skill) => {
        skill.subSkill.forEach((sub) => {
          subSkills.push(sub);
        });
      });
      setServiceSkill(subSkills);
    } catch (error) {
      showAlert("Error fetching services", "danger");
    } finally {
      setIsLoading(false);
    }
  };

  const fetchPortfolio = async (id) => {
    try {
      setIsLoading(true);
      const response = await axios.get(
        `${BaseURL}/api/portfolio/marketplace/fetchmarket/${id}`,
        {
          headers: {
            "auth-token": localStorage.getItem("p@token"),
          },
        }
      );
      const portfolioData = response.data;
      setFormData({
        title: portfolioData.Title || "",
        category: portfolioData.Category || "Category",
        subcategory: portfolioData.SubCategory || "SubCategory",
        keywords: portfolioData.Keywords || [],
        keyword: "",
      });
      setServiceSelect(portfolioData.Category);
    } catch (error) {
      showAlert(
        error.response?.data?.message || "Error fetching portfolio data",
        "danger"
      );
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchIndustry();
    const query = new URLSearchParams(location.search);
    const editId = query.get("editid");
    if (editId) {
      fetchPortfolio(editId);
    }
  }, []);

  useEffect(() => {
    if (ServiceSelect && ServiceSelect !== "Category") {
      fetchServiceSkills();
    }
  }, [ServiceSelect]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSelectChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
      ...(name === "category" && { subcategory: "SubCategory" }),
    }));
    if (name === "category") {
      setServiceSelect(value);
    }
  };

  const handleAddKeyword = () => {
    const { keyword, keywords } = formData;
    if (!keyword.trim()) {
      showAlert("Please enter a keyword", "warning");
      return;
    }
    if (keywords.includes(keyword.trim())) {
      showAlert("Keyword already exists", "warning");
      return;
    }
    if (keywords.length >= 10) {
      showAlert("Maximum 10 keywords allowed", "warning");
      return;
    }
    setFormData((prev) => ({
      ...prev,
      keywords: [...prev.keywords, keyword.trim()],
      keyword: "",
    }));
  };

  const handleRemoveKeyword = (index) => {
    setFormData((prev) => ({
      ...prev,
      keywords: prev.keywords.filter((_, idx) => idx !== index),
    }));
  };

  const validateForm = () => {
    const { title, category, subcategory, keywords } = formData;

    if (!title.trim()) {
      showAlert("Please enter a title", "warning");
      return false;
    }
    if (category === "Category") {
      showAlert("Please select a category", "warning");
      return false;
    }
    if (subcategory === "SubCategory") {
      showAlert("Please select a subcategory", "warning");
      return false;
    }
    if (keywords.length === 0) {
      showAlert("Please add at least one keyword", "warning");
      return false;
    }
    return true;
  };

  const handleNextClick = async () => {
    try {
      if (!validateForm()) return;

      setIsLoading(true);
      const query = new URLSearchParams(location.search);
      const editId = query.get("editid");

      if (editId) {
        navigate(
          `/user-dashboard/profile/marketplace_details?editid=${editId}`,
          {
            state: { formData },
          }
        );
      } else {
        navigate("/user-dashboard/profile/marketplace_details", {
          state: { formData },
        });
      }
    } catch (error) {
      showAlert("Navigation failed", "danger");
      setIsLoading(false);
    }
  };

  if (isInitializing) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primarygreen"></div>
      </div>
    );
  }

  return (
    <div className="flex items-center font-pop flex-col">
      <div
        className={`rounded-2xl py-6 px-6 my-6 w-[95%] md:w-[70%] xl:w-[40%] flex flex-col gap-4
            ${theme === "dark" ? "bg-black text-white" : "bg-white"}`}
      >
        <h1 className="font-semibold text-xl text-center">Create portfolio</h1>
        <div className="flex flex-col gap-4">
          <input
            type="text"
            id="title"
            name="title"
            placeholder="Give your project brief a title"
            className={`w-full px-6 border-2 border-transparent font-pop text-base rounded-full  py-2 focus:border-primarygreen focus:outline-none
                        ${
                          theme === "dark"
                            ? "bg-black text-white border border-white"
                            : "bg-[#E8E8E8]"
                        }`}
            value={formData.title}
            onChange={handleInputChange}
          />
          <select
            name="category"
            value={formData.category}
            onChange={handleSelectChange}
            className={`w-full px-6 border-2 border-transparent font-pop text-base rounded-full  py-2 focus:border-primarygreen focus:outline-none
                                           ${
                                             theme === "dark"
                                               ? "bg-black text-white border border-white"
                                               : "bg-[#E8E8E8]"
                                           }`}
          >
            <option value="Category">Category</option>
            {IndustryList?.map((item, index) => (
              <option value={item._id} key={index}>
                {item.name}
              </option>
            ))}
          </select>
          <select
            name="subcategory"
            value={formData.subcategory}
            onChange={handleSelectChange}
            className={`w-full px-6 border-2 border-transparent font-pop text-base rounded-full  py-2 focus:border-primarygreen focus:outline-none
                                           ${
                                             theme === "dark"
                                               ? "bg-black text-white border border-white"
                                               : "bg-[#E8E8E8]"
                                           }`}
          >
            <option value="SubCategory">SubCategory</option>
            {ServiceSkill?.map((item, index) => (
              <option value={item._id} key={index}>
                {item.name}
              </option>
            ))}
          </select>
          <div className="flex gap-2 items-center">
            <input
              type="text"
              id="keyword"
              name="keyword"
              placeholder="Keywords"
              className={`w-full px-6 border-2 border-transparent font-pop text-base rounded-full  py-2 focus:border-primarygreen focus:outline-none ${
                theme === "dark"
                  ? "bg-black text-white border-white"
                  : "bg-[#E8E8E8]"
              }`}
              value={formData.keyword}
              onChange={handleInputChange}
            />
            <button
              className={`bg-primarygreen text-white px-4 py-2 rounded-full hover:bg-transparent border border-primarygreen hover:text-primarygreen`}
              onClick={handleAddKeyword}
            >
              Add
            </button>
          </div>
          <ul className="flex flex-wrap gap-2">
            {formData.keywords.map((keyword, index) => (
              <li
                key={index}
                className={` rounded-full px-4 py-1 flex items-center justify-between
                            ${
                              theme === "dark"
                                ? "bg-black text-white border border-white"
                                : "bg-gray-200"
                            }`}
              >
                <span>{keyword}</span>
                <button
                  className="ml-2 text-red-500 hover:text-red-700"
                  onClick={() => handleRemoveKeyword(index)}
                >
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M6 18L18 6M6 6l12 12"
                    ></path>
                  </svg>
                </button>
              </li>
            ))}
          </ul>
          <button
            className={`my-4 px-2 md:px-6 bg-green-500 hover:bg-transparent hover:bg-white-500 font-medium hover:text-black font-pop text-sm md:text-lg py-2 md:py-3 border border-black-500 hover:border-green-500 rounded-full text-white
                         ${
                           theme === "dark"
                             ? "hover:text-white"
                             : "hover:text-black"
                         }
                         ${
                           isLoading
                             ? "opacity-50 cursor-not-allowed"
                             : "cursor-pointer"
                         }`}
            onClick={handleNextClick}
            disabled={isLoading}
          >
            {isLoading ? (
              <div className="flex items-center justify-center">
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
              </div>
            ) : (
              "Next"
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default MarketPlaceCreation;
